import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { PhotoCard, SourceScope } from '@photos/shared';
import { groupByDay, type DayGroup } from '../lib/days';
import { fmtCount, fmtDayHeader } from '../lib/format';
import { justify, targetRowHeight } from '../lib/justify';
import { VISIBILITY_LOOK } from '../lib/visibility';
import { thumbhashDataUrl } from '../lib/thumbhash';
import { useSession } from '../state/session';
import { Icon } from './Icon';

const GAP = 4;

export const fromFlock = (p: Pick<PhotoCard, 'source' | 'origin'>) => p.source === 'flock' || p.origin === 'flock';

interface TileProps {
  photo: PhotoCard;
  width: number;
  height: number;
  selected: boolean;
  showFlockBadge: boolean;
  alt: string;
  onClick(e: MouseEvent, id: string, viaCheck: boolean): void;
}

const Tile = memo(function Tile({ photo: p, width, height, selected, showFlockBadge, alt, onClick }: TileProps) {
  const look = VISIBILITY_LOOK[p.visibility];
  const src = width > 500 || height > 500 ? p.previewUrl : p.thumbUrl;
  const ph = thumbhashDataUrl(p.thumbhash);
  return (
    <div
      className={`tile${selected ? ' sel' : ''}`}
      data-id={p.id}
      style={{ width, height }}
      role="button"
      tabIndex={0}
      aria-label={alt}
      aria-pressed={selected}
      onClick={(e) => onClick(e, p.id, false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) onClick(e as unknown as MouseEvent, p.id, false);
      }}
    >
      <button
        type="button"
        className="ck"
        aria-label={selected ? 'Deselect' : 'Select'}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e, p.id, true);
        }}
      >
        <Icon name="check" />
      </button>
      {p.picked && (
        <span className="badge pick" title="Pick">
          <Icon name="star" />
        </span>
      )}
      {showFlockBadge && fromFlock(p) && (
        <span className="badge src">
          <Icon name="flock" /> {p.uploaderName}
        </span>
      )}
      {look.badge && (
        <span className="badge lock" data-vis={p.visibility}>
          <Icon name={look.badge.icon} /> {look.badge.label}
        </span>
      )}
      <img
        loading="lazy"
        decoding="async"
        alt={alt}
        src={src}
        style={ph ? { backgroundImage: `url(${ph})`, backgroundSize: 'cover' } : undefined}
      />
    </div>
  );
});

/** How the grid is split into sections: by capture day, by upload day, or one section (e.g. sorted by file name). */
export type Grouping = 'taken' | 'uploaded' | 'none';

interface DayProps {
  group: DayGroup<PhotoCard>;
  /** heading instead of the day (one-section grids) */
  label?: string;
  width: number;
  target: number;
  selected: ReadonlySet<string>;
  source: SourceScope;
  onTileClick: TileProps['onClick'];
  onSelectDay(ids: string[]): void;
}

const DaySection = memo(function DaySection({ group, label, width, target, selected, source, onTileClick, onSelectDay }: DayProps) {
  const { tagIndex } = useSession();
  const rows = useMemo(
    () => justify(group.items.map((p) => ({ p, ar: p.width / p.height })), width, target, GAP),
    [group.items, width, target],
  );
  const events = useMemo(
    () => [...new Set(group.items.map((p) => tagIndex.eventLabel(p.tags)).filter(Boolean))].join(', '),
    [group.items, tagIndex],
  );
  const n = group.items.length;
  return (
    <section className="day">
      <div className="dayhead">
        <span className="d">{label ?? fmtDayHeader(group.date)}</span>
        {events && group.key !== 'all' && <span className="ev">{events}</span>}
        <span className="dn">
          {n} {n === 1 ? 'photo' : 'photos'}
        </span>
        <button type="button" className="sel" onClick={() => onSelectDay(group.items.map((p) => p.id))}>
          {group.key === 'all' ? 'Select all' : 'Select day'}
        </button>
      </div>
      <div className="rows">
        {rows.map((r, i) => (
          <div className="jrow" key={i}>
            {r.tiles.map(({ item: { p }, width: w, height: h }) => (
              <Tile
                key={p.id}
                photo={p}
                width={w}
                height={h}
                selected={selected.has(p.id)}
                showFlockBadge={source !== 'flock'}
                alt={tagIndex.eventLabel(p.tags) || p.fileName}
                onClick={onTileClick}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
});

interface PhotoGridProps {
  items: PhotoCard[];
  total: number;
  selected: ReadonlySet<string>;
  source: SourceScope;
  hasMore: boolean;
  loadMore(): void;
  onTileClick: TileProps['onClick'];
  onSelectDay(ids: string[]): void;
  grouping?: Grouping;
}

/** Justified photo grid grouped by day, with an infinite-scroll sentinel. */
export function PhotoGrid({ items, total, selected, source, hasMore, loadMore, onTileClick, onSelectDay, grouping = 'taken' }: PhotoGridProps) {
  const grid = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [vw, setVw] = useState(() => window.innerWidth);

  useLayoutEffect(() => {
    const el = grid.current;
    if (!el) return;
    setWidth(el.clientWidth || 1000);
    let t = 0;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      clearTimeout(t);
      t = window.setTimeout(() => {
        setWidth(el.clientWidth || 1000);
        setVw(window.innerWidth);
      }, 120);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, []);

  const loadRef = useRef(loadMore);
  loadRef.current = loadMore;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((es) => {
      if (es[0]?.isIntersecting) loadRef.current();
    }, { rootMargin: '800px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, items.length]);

  // Keep unchanged day groups referentially stable so loading another page doesn't re-layout every day.
  const prevGroups = useRef(new Map<string, DayGroup<PhotoCard>>());
  const groups = useMemo(() => {
    const split =
      grouping === 'none'
        ? items.length
          ? [{ key: 'all', date: new Date(0), items: [...items] }]
          : []
        : groupByDay(items, grouping === 'uploaded' ? (p) => p.uploadedAt : (p) => p.takenAt);
    const out = split.map((g) => {
      const old = prevGroups.current.get(g.key + g.items[0]!.id);
      return old && old.items.length === g.items.length && old.items.every((p, i) => p === g.items[i]) ? old : g;
    });
    prevGroups.current = new Map(out.map((g) => [g.key + g.items[0]!.id, g]));
    return out;
  }, [items, grouping]);
  const target = targetRowHeight(vw);

  return (
    <>
      <div id="grid" ref={grid} className={selected.size ? 'selecting' : undefined}>
        {width > 0 &&
          groups.map((g) => (
            <DaySection
              key={g.key + g.items[0]!.id}
              group={g}
              label={grouping === 'none' ? 'Sorted by file name' : grouping === 'uploaded' ? `Uploaded ${fmtDayHeader(g.date)}` : undefined}
              width={width}
              target={target}
              selected={selected}
              source={source}
              onTileClick={onTileClick}
              onSelectDay={onSelectDay}
            />
          ))}
      </div>
      <div className="sentinel" id="sentinel" ref={sentinel}>
        {hasMore ? <div className="spinner" /> : <span>That’s everything · {fmtCount(total)} photos</span>}
      </div>
    </>
  );
}
